import React, { createContext, useState, useContext, useEffect } from 'react';
import { alertsAPI } from '../services/api';

const AlertContext = createContext();

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within AlertProvider');
  }
  return context;
};

export const AlertProvider = ({ children }) => {
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({
    totalAlerts: 0,
    activeAlerts: 0,
    resolvedAlerts: 0,
    criticalAlerts: 0,
    alertsByType: {},
    alertsBySeverity: {},
    recentAlerts: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selectedAlert, setSelectedAlert] = useState(null);

  // Get all alerts
  const fetchAlerts = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await alertsAPI.getAll();
      setAlerts(response.data || []);
    } catch (err) {
      console.error('Fetch alerts error:', err);
      setError(err.message || 'Failed to fetch alerts. Please check if the backend server is running.');
    } finally {
      setLoading(false);
    }
  };

  // Get alert statistics
  const fetchStats = async () => {
    try {
      const response = await alertsAPI.getStats();
      setStats(response.data);
    } catch (err) {
      console.error('Fetch stats error:', err);
      // Fallback to calculating from alerts if stats endpoint fails
      try {
        const alertsResponse = await alertsAPI.getAll();
        if (alertsResponse.success) {
          const alertsData = alertsResponse.data || [];
          
          // Calculate stats manually
          const totalAlerts = alertsData.length;
          const activeAlerts = alertsData.filter(a => a.status === 'active').length;
          const resolvedAlerts = alertsData.filter(a => a.status === 'resolved').length;
          const criticalAlerts = alertsData.filter(a => 
            (a.severity === 'critical' || a.severity === 'high') && a.status === 'active'
          ).length;
          
          // Calculate alerts by type
          const alertsByType = {};
          alertsData.forEach(alert => {
            alertsByType[alert.type] = (alertsByType[alert.type] || 0) + 1;
          });
          
          // Calculate alerts by severity
          const alertsBySeverity = {};
          alertsData.forEach(alert => {
            alertsBySeverity[alert.severity] = (alertsBySeverity[alert.severity] || 0) + 1;
          });
          
          // Calculate recent alerts (last 7 days)
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          const recentAlerts = alertsData.filter(alert => 
            new Date(alert.createdAt) >= weekAgo
          ).length;

          setStats({
            totalAlerts,
            activeAlerts,
            resolvedAlerts,
            criticalAlerts,
            alertsByType,
            alertsBySeverity,
            recentAlerts
          });
        }
      } catch (fallbackErr) {
        console.error('Fallback stats calculation error:', fallbackErr);
      }
    }
  };

  // Get single alert
  const fetchAlert = async (id) => {
    setLoading(true);
    try {
      const response = await alertsAPI.getById(id);
      setSelectedAlert(response.data);
      return response.data;
    } catch (err) {
      console.error('Fetch alert error:', err);
      setError(err.message || 'Failed to fetch alert');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Create alert
  const createAlert = async (alertData) => {
    try {
      const response = await alertsAPI.create(alertData);
      // Update local state
      setAlerts(prev => [response.data, ...prev]);
      fetchStats();
      return { success: true, data: response.data };
    } catch (err) {
      return { 
        success: false, 
        error: err.message || 'Failed to create alert' 
      };
    }
  };

  // Acknowledge alert (for drivers)
  const acknowledgeAlert = async (id) => {
    try {
      const response = await alertsAPI.acknowledge(id);
      // Update local state
      setAlerts(prev => prev.map(alert => 
        alert._id === id ? response.data : alert
      ));
      return { success: true };
    } catch (err) {
      return { 
        success: false, 
        error: err.message || 'Failed to acknowledge alert' 
      };
    }
  };

  // Resolve alert (for admins)
  const resolveAlert = async (id) => {
    try {
      const response = await alertsAPI.resolve(id);
      // Update local state
      setAlerts(prev => prev.map(alert => 
        alert._id === id ? response.data : alert
      ));
      fetchStats();
      return { success: true };
    } catch (err) {
      return { 
        success: false, 
        error: err.message || 'Failed to resolve alert' 
      };
    }
  };

  // Delete alert (for admins)
  const deleteAlert = async (id) => {
    try {
      await alertsAPI.delete(id);
      // Update local state
      setAlerts(prev => prev.filter(alert => alert._id !== id));
      setSelectedAlert(null);
      fetchStats();
      return { success: true };
    } catch (err) {
      return { 
        success: false, 
        error: err.message || 'Failed to delete alert' 
      };
    }
  };

  // Filter alerts based on current filter
  const getFilteredAlerts = () => {
    const filtered = alerts.filter(alert => {
      switch (filter) {
        case 'active':
          return alert.status === 'active';
        case 'resolved':
          return alert.status === 'resolved';
        case 'critical':
          return (alert.severity === 'critical' || alert.severity === 'high') && alert.status === 'active';
        default:
          return true;
      }
    });
    return filtered;
  };

  // Initialize data
  useEffect(() => {
    fetchAlerts();
    fetchStats();
    
    // Set up WebSocket connection for real-time updates
    const setupWebSocket = () => {
      try {
        const wsUrl = process.env.REACT_APP_WEBSOCKET_URL || 'wss://fleet-county-backend.onrender.com';
        const ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          console.log('WebSocket connected');
          const token = localStorage.getItem('token');
          if (token) {
            ws.send(JSON.stringify({
              type: 'authenticate',
              token: token
            }));
          }
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'alert' && data.data) {
              // New alert received via WebSocket
              setAlerts(prev => [data.data, ...prev]);
              fetchStats();
            }
          } catch (err) {
            console.error('WebSocket message error:', err);
          }
        };
        
        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
        };
        
        ws.onclose = () => {
          console.log('WebSocket disconnected');
        };
        
        return () => {
          ws.close();
        };
      } catch (err) {
        console.error('WebSocket setup error:', err);
      }
    };

    setupWebSocket();
  }, []);

  const value = {
    alerts: getFilteredAlerts(),
    allAlerts: alerts,
    stats,
    loading,
    error,
    filter,
    selectedAlert,
    setFilter,
    setSelectedAlert,
    fetchAlerts,
    fetchStats,
    fetchAlert,
    createAlert,
    acknowledgeAlert,
    resolveAlert,
    deleteAlert,
    refetch: () => {
      fetchAlerts();
      fetchStats();
    }
  };

  return (
    <AlertContext.Provider value={value}>
      {children}
    </AlertContext.Provider>
  );
};